import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Button } from "./ui/button";
import { LOGO_URL, NAV_LINKS } from "../lib/constants";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all border-b ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-slate-200" : "bg-white/60 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-2 group flex-shrink-0">
          <img src={LOGO_URL} alt="PRISM" className="h-12 w-auto max-w-none" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.name.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-[#0A2540]" : "text-slate-600 hover:text-[#0A2540]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.name}
                  {isActive && (
                    <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-[#0A2540]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button
            asChild
            data-testid="navbar-cta-button"
            className="rounded-md bg-[#0A2540] hover:bg-[#0F3A66] text-white h-10 px-5 gap-1"
          >
            <Link to="/contact">
              Request Consultation <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className="lg:hidden p-2 text-slate-700"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-base font-medium border-b border-slate-100 ${
                    isActive ? "text-[#0A2540]" : "text-slate-600"
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}
            <Button
              asChild
              data-testid="mobile-cta-button"
              className="mt-3 rounded-md bg-[#0A2540] hover:bg-[#0F3A66] text-white"
            >
              <Link to="/contact">Request Consultation</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
